/**
 * Mettre les gammes à jour d'après une version de tarif (lot C2).
 *
 * Le serveur compare, pour chaque gamme du fabricant, les lignes de sa version en vigueur à cette version de tarif,
 * PAR RÉFÉRENCE : une décision vaut pour toutes les gammes qui portent la référence. Cet écran montre l'écart
 * (texte actuel → texte du tarif, prix actuel → prix du tarif), coche d'office les compléments, laisse décochées les
 * reformulations et tout doute, et n'envoie QUE ces décisions. **Rien n'est écrit dans une gamme existante** : le
 * serveur crée un BROUILLON par gamme concernée, que l'on ouvre, retouche si besoin (le texte du tarif est lui-même
 * coupé à 40 caractères) et publie par le geste habituel.
 *
 * Les cases, les filtres et la date d'effet sont du travail non enregistré : fermer l'onglet ou quitter par le menu
 * demande confirmation. Les barres de boutons et les messages sont collés en bas, là où l'on vient de cliquer.
 */
import { useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  api,
  type BilanApplicationTarif,
  type BrouillonEnAttenteTarif,
  type GroupeReferenceTarif,
  type RevueTarifGammes,
  type TarifDetail,
  type VersionTarif,
} from '../api/client';
import type { DroitsGammes } from '../domaine/droits';
import { signalerModifications } from '../domaine/garde-modifications';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import {
  LIBELLE_FILTRE,
  TITRE_REFUS,
  accordFr,
  basculer,
  caseDisponible,
  choixModifies,
  choixParDefaut,
  cocherLesVisibles,
  comptes,
  decisionsDe,
  erreurDeLaDate,
  estRienAChanger,
  filtrer,
  gammesAEnvoyer,
  lienVersGamme,
  messageBilan,
  nombreParFiltre,
  variationSuspecte,
  type ChampDecision,
  type ChoixParCle,
  type DroitsRevue,
  type Filtre,
} from '../domaine/gammes-tarif';
import {
  bilanPublication,
  confirmationPublicationGroupee,
  publiables,
  publierEnSerie,
} from '../domaine/publication-groupee';
import { dateFrTarif, eurosFr, formaterVariation, lignesFr, nombreFr } from '../domaine/tarifs';
import { Champ, ErreurFormulaire } from './Formulaire';

const FILTRES: readonly Filtre[] = ['aCompleter', 'reformulees', 'prix', 'aucunChangement', 'tout'];

const MESSAGE_GARDE =
  'Vos choix de mise à jour des gammes ne sont pas encore appliqués : quitter maintenant les fera perdre.';

/** Le premier filtre qui a quelque chose à montrer : ce qu'il y a à regarder d'abord. */
function filtreInitial(r: RevueTarifGammes): Filtre {
  const n = nombreParFiltre(r.groupes);
  return (['aCompleter', 'reformulees', 'prix'] as const).find((f) => n[f] > 0) ?? 'tout';
}

export function MajGammesTarif({
  tarif,
  version,
  droits,
  onFermer,
  onBrouillonsCrees,
}: {
  tarif: TarifDetail;
  version: VersionTarif;
  droits: DroitsGammes;
  onFermer: () => void;
  /** Les brouillons viennent d'être créés : le parent annonce le résultat. */
  onBrouillonsCrees: (message: string) => void;
}) {
  const [revue, setRevue] = useState<RevueTarifGammes | null>(null);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [relecture, setRelecture] = useState(0);
  const [choix, setChoix] = useState<ChoixParCle>({});
  const [initiaux, setInitiaux] = useState<ChoixParCle>({});
  const [filtre, setFiltre] = useState<Filtre>('aCompleter');
  const [recherche, setRecherche] = useState('');
  const [ouvertes, setOuvertes] = useState<ReadonlySet<string>>(new Set());
  const [validFrom, setValidFrom] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [bilan, setBilan] = useState<BilanApplicationTarif | null>(null);
  // Les brouillons issus de CETTE version de tarif qui attendent leur publication : relus à part de la revue, pour
  // rester à jour après la création des brouillons (le bilan reste affiché) et après une publication.
  const [enAttente, setEnAttente] = useState<readonly BrouillonEnAttenteTarif[]>([]);
  const [progression, setProgression] = useState<{ readonly faits: number; readonly total: number } | null>(null);
  const [bilanPub, setBilanPub] = useState<{ readonly succes: string | null; readonly refus: string | null } | null>(null);

  // Sans la revue, on ne sait pas encore si le serveur rend les prix : aucun droit d'écrire un prix n'est présumé.
  const droitsRevue: DroitsRevue = useMemo(
    () => ({ peutEcrirePrix: droits.peutEcrirePrix && (revue?.avecPrix ?? false) }),
    [droits.peutEcrirePrix, revue?.avecPrix],
  );

  useEffect(() => {
    let annule = false;
    setRevue(null);
    setErreurLecture(null);
    api
      .revueTarifGammes(tarif.id, version.id)
      .then((r) => {
        if (annule) return;
        const depart = choixParDefaut(r.groupes, { peutEcrirePrix: droits.peutEcrirePrix && r.avecPrix });
        setRevue(r);
        setEnAttente(r.brouillonsEnAttente);
        setChoix(depart);
        setInitiaux(depart);
        setValidFrom(r.dateEffetProposee);
        setFiltre(filtreInitial(r));
        setOuvertes(new Set());
      })
      .catch((e: unknown) => {
        if (!annule) setErreurLecture(e instanceof ApiError || e instanceof Error ? e.message : 'Lecture impossible.');
      });
    return () => {
      annule = true;
    };
  }, [tarif.id, version.id, relecture, droits.peutEcrirePrix]);

  const aDuTravail = revue !== null && bilan === null && choixModifies(initiaux, choix);

  // Du travail non enregistré : le menu et la fermeture de l'onglet doivent le savoir.
  useEffect(() => {
    signalerModifications(aDuTravail, MESSAGE_GARDE);
    return () => signalerModifications(false);
  }, [aDuTravail]);

  // Choix non appliqués, ou publication en cours (chaque gamme est atomique, mais on ne coupe pas la série en route).
  const aGarder = aDuTravail || progression !== null;
  useEffect(() => {
    if (!aGarder) return;
    const avantFermeture = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', avantFermeture);
    return () => window.removeEventListener('beforeunload', avantFermeture);
  }, [aGarder]);

  const visibles = useMemo(() => (revue ? filtrer(revue.groupes, filtre, recherche) : []), [revue, filtre, recherche]);
  const nombres = useMemo(() => (revue ? nombreParFiltre(revue.groupes) : null), [revue]);
  const totaux = useMemo(() => (revue ? comptes(revue.groupes, choix, droitsRevue) : null), [revue, choix, droitsRevue]);
  const gammesEnvoyees = useMemo(
    () => (revue ? gammesAEnvoyer(revue.groupes, choix, revue.gammes, droitsRevue) : []),
    [revue, choix, droitsRevue],
  );
  const erreurDate = revue ? erreurDeLaDate(validFrom, gammesEnvoyees, revue.gammes) : null;
  const decisions = useMemo(() => (revue ? decisionsDe(revue.groupes, choix, droitsRevue) : []), [revue, choix, droitsRevue]);

  const peutCreer = revue !== null && !enCours && bilan === null && decisions.length > 0 && gammesEnvoyees.length > 0 && erreurDate === null;

  /** Relit les brouillons en attente SANS toucher aux choix ni au bilan affichés. */
  const relireLesBrouillons = async () => {
    const r = await api.revueTarifGammes(tarif.id, version.id);
    setEnAttente(r.brouillonsEnAttente);
  };

  const creer = async () => {
    if (!revue || !peutCreer) return;
    setEnCours(true);
    setErreurAction(null);
    setBilanPub(null);
    try {
      const resultat = await api.appliquerTarifGammes(tarif.id, version.id, {
        decisions,
        gammes: gammesEnvoyees,
        validFrom,
      });
      setBilan(resultat);
      if (resultat.brouillons > 0) {
        onBrouillonsCrees(messageBilan(resultat));
        // Les brouillons à publier sont retrouvés par le serveur (date d'effet, version en vigueur) : on les relit.
        await relireLesBrouillons().catch(() => undefined);
      }
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Création impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const aPublier = useMemo(() => publiables(enAttente), [enAttente]);

  /** Publie d'un coup les brouillons en attente : UNE confirmation, puis chaque gamme l'une après l'autre. */
  const publier = async () => {
    if (enCours || aPublier.length === 0) return;
    if (!window.confirm(confirmationPublicationGroupee(enAttente, aujourdhuiLocal()))) return;
    setEnCours(true);
    setErreurAction(null);
    setBilanPub(null);
    setProgression({ faits: 0, total: aPublier.length });
    try {
      const resultats = await publierEnSerie(
        aPublier,
        (b) => api.publishMaintenanceVersion(b.profileId, b.versionId),
        (faits, total) => setProgression({ faits, total }),
      );
      setBilanPub(bilanPublication(resultats));
      // Les gammes publiées ont une nouvelle version en vigueur : tout ce que la revue montrait est périmé.
      setBilan(null);
      setRelecture((n) => n + 1);
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Publication impossible.'));
    } finally {
      setProgression(null);
      setEnCours(false);
    }
  };

  const basculerOuverte = (cle: string) =>
    setOuvertes((o) => {
      const suivant = new Set(o);
      if (suivant.has(cle)) suivant.delete(cle);
      else suivant.add(cle);
      return suivant;
    });

  const fermer = () => {
    if (aDuTravail && !window.confirm(`${MESSAGE_GARDE}\n\nFermer quand même ?`)) return;
    onFermer();
  };

  if (erreurLecture) {
    return (
      <div className="maj-gammes-tarif">
        <div className="message erreur">{erreurLecture}</div>
        <div className="saisie-ligne">
          <button type="button" className="secondaire" onClick={onFermer}>
            Fermer
          </button>
        </div>
      </div>
    );
  }
  if (!revue || !totaux || !nombres) {
    return <div className="vide">Comparaison des gammes au tarif en cours…</div>;
  }

  const peutVoirPrix = revue.avecPrix;
  // Juste après la création, le bilan liste déjà chaque brouillon (avec ses liens) : ne pas le répéter dans le bloc de publication.
  const idsDuBilan = new Set(bilan?.gammes.flatMap((g) => (g.brouillon ? [g.brouillon.id] : [])) ?? []);
  const toutesDansLeBilan = bilan !== null && enAttente.every((b) => idsDuBilan.has(b.versionId));

  return (
    <div className="maj-gammes-tarif">
      <div className="message info">
        Les gammes de <strong>{tarif.partyName}</strong> sont comparées à la <strong>version {revue.tarif.versionNumber}</strong>{' '}
        de « {tarif.label} » (
        {revue.tarif.aVenir ? `à venir : prend effet le ${dateFrTarif(revue.tarif.validFrom)}` : `en vigueur depuis le ${dateFrTarif(revue.tarif.validFrom)}`}
        ). <strong>Rien n’est écrit dans une gamme existante</strong> : cochez ce que vous voulez appliquer, l’application crée
        un <strong>brouillon par gamme concernée</strong>, que vous ouvrez, retouchez si besoin, puis publiez.
      </div>

      {/* Le résultat de la création, EN HAUT : c'est ce qu'on veut lire (avec un lien vers chaque brouillon), pas sous le tableau. */}
      {bilan && <BilanDeLaCreation bilan={bilan} />}

      {/* Le résultat de la dernière publication groupée. */}
      {bilanPub && (
        <div className="maj-gammes-bilan">
          {bilanPub.succes && <div className="message succes">{bilanPub.succes} Les machines rattachées suivent la gamme.</div>}
          {bilanPub.refus && <div className="message erreur">{bilanPub.refus}</div>}
        </div>
      )}

      {!bilan && (
        <p className="aide">
          {accordFr(revue.groupes.length, 'référence comparée', 'références comparées')} sur{' '}
          {accordFr(revue.gammes.length, 'gamme', 'gammes')}.
          {revue.horsTarif.length > 0 && ` ${accordFr(revue.horsTarif.length, 'référence absente du tarif', 'références absentes du tarif')} : aucune proposition.`}
          {revue.ambigues.length > 0 && ` ${accordFr(revue.ambigues.length, 'référence ambiguë', 'références ambiguës')} (plusieurs lignes du tarif pour la même clé) : aucune proposition.`}
        </p>
      )}

      {enAttente.length > 0 && (
        <div className="message avertissement maj-gammes-attente">
          <strong>
            {accordFr(enAttente.length, 'brouillon issu de cette version de tarif attend', 'brouillons issus de cette version de tarif attendent')}{' '}
            sa publication{toutesDansLeBilan ? '.' : ' :'}
          </strong>
          {!toutesDansLeBilan && (
            <ul className="details-erreur">
              {enAttente.map((b) => (
                <li key={b.versionId}>
                  <a href={lienVersGamme(b.profileId, b.versionId)} target="_blank" rel="noreferrer">
                    {b.code} — brouillon v{b.versionNumber}
                  </a>
                  {b.perimee && ' — la version en vigueur de la gamme a changé depuis : à relire avant de publier, non publié d’un coup.'}
                </li>
              ))}
            </ul>
          )}
          {aPublier.length > 0 &&
            (droits.peutPublier ? (
              <div className="saisie-ligne">
                <button type="button" disabled={enCours} onClick={() => void publier()}>
                  {progression
                    ? `Publication ${progression.faits}/${progression.total}…`
                    : aPublier.length > 1
                      ? `Publier les ${aPublier.length} brouillons`
                      : 'Publier le brouillon'}
                </button>
                <span className="aide">Une seule confirmation, puis chaque gamme est publiée l’une après l’autre.</span>
              </div>
            ) : (
              <p className="aide">Seul un compte autorisé à publier les gammes peut les publier : ouvrez chaque brouillon pour le relire.</p>
            ))}
        </div>
      )}

      {revue.ignorees.length > 0 && !bilan && (
        <details className="maj-gammes-ignorees">
          <summary>
            {accordFr(revue.ignorees.length, 'gamme non traitée', 'gammes non traitées')} (brouillon déjà ouvert, version à venir…)
          </summary>
          <ul className="details-erreur">
            {revue.ignorees.map((g) => (
              <li key={g.profileId}>
                <strong>{g.code}</strong> — {g.detail}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Une fois les brouillons créés, les choix sont appliqués : on ne montre plus le tableau (« Relire la revue » le rend). */}
      {!bilan && (
        <>
      {/* Les filtres et la recherche */}
      <div className="maj-gammes-outils">
        <div className="chips-secondaires" role="group" aria-label="Filtrer les références">
          {/* Sans le droit de voir les prix, il n'existe aucun cas de prix : la pastille n'aurait rien à compter. */}
          {FILTRES.filter((f) => f !== 'prix' || peutVoirPrix).map((f) => (
            <button
              key={f}
              type="button"
              className={`chip${f === filtre ? ' actif' : ''}`}
              aria-pressed={f === filtre}
              onClick={() => setFiltre(f)}
            >
              {LIBELLE_FILTRE[f]} <span className="aide">{nombres[f]}</span>
            </button>
          ))}
        </div>
        <input
          type="text"
          aria-label="Chercher une référence, un texte ou une gamme"
          placeholder="Chercher : référence, texte, gamme…"
          value={recherche}
          disabled={enCours}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </div>

      <div className="saisie-ligne maj-gammes-cocher">
        <span className="aide">{accordFr(visibles.length, 'référence affichée', 'références affichées')} :</span>
        <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setChoix((c) => cocherLesVisibles(c, visibles, 'designation', true, droitsRevue))}>
          Cocher les textes
        </button>
        <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setChoix((c) => cocherLesVisibles(c, visibles, 'designation', false, droitsRevue))}>
          Décocher les textes
        </button>
        {droitsRevue.peutEcrirePrix && (
          <>
            <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setChoix((c) => cocherLesVisibles(c, visibles, 'prix', true, droitsRevue))}>
              Cocher les prix
            </button>
            <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setChoix((c) => cocherLesVisibles(c, visibles, 'prix', false, droitsRevue))}>
              Décocher les prix
            </button>
          </>
        )}
        <button type="button" className="secondaire discret" disabled={enCours} onClick={() => setChoix(initiaux)}>
          Revenir aux cases d’origine
        </button>
      </div>

      {visibles.length === 0 ? (
        <div className="vide">Aucune référence ne correspond à ce filtre{recherche.trim() ? ` et à « ${recherche.trim()} »` : ''}.</div>
      ) : (
        <div className="tableau-defilant">
          <table className="tableau-ecarts maj-gammes-table">
            <thead>
              <tr>
                <th>Texte</th>
                <th>Référence</th>
                <th>Texte actuel → texte du tarif</th>
                {peutVoirPrix && <th>Prix actuel → tarif</th>}
                {droitsRevue.peutEcrirePrix && <th>Prix</th>}
                <th>Gammes</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((g) => (
                <LigneDeReference
                  key={g.cle}
                  groupe={g}
                  choix={choix[g.cle]}
                  droits={droitsRevue}
                  peutVoirPrix={peutVoirPrix}
                  ouverte={ouvertes.has(g.cle)}
                  desactive={enCours}
                  onBasculer={(champ, valeur) => setChoix((c) => basculer(c, g.cle, champ, valeur))}
                  onOuvrir={() => basculerOuverte(g.cle)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}

      <div className="editeur-barre">
        <ErreurFormulaire
          message={erreurAction?.message ?? null}
          details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
        />
        {!bilan && (
          <Champ
            libelle="Date d’effet des brouillons"
            requis
            aide={
              revue.tarif.aVenir
                ? 'Le tarif prend effet plus tard : les gammes sont préparées pour cette date, l’ancienne version reste en vigueur d’ici là.'
                : 'Aujourd’hui par défaut : la version publiée remplace la version en vigueur à partir de cette date.'
            }
          >
            <input type="date" value={validFrom} disabled={enCours} onChange={(e) => setValidFrom(e.target.value)} />
            {erreurDate && <div className="message erreur">{erreurDate}</div>}
          </Champ>
        )}
        <div className="editeur-barre-actions">
          <span className={aDuTravail ? 'etat attente' : 'aide'}>
            {bilan
              ? 'Brouillons créés : voir le détail ci-dessus.'
              : decisions.length === 0
                ? 'Aucune référence cochée'
                : `${accordFr(totaux.references, 'référence', 'références')} · ${lignesFr(totaux.lignes)} · ${accordFr(totaux.gammes, 'gamme', 'gammes')}`}
          </span>
          <div className="saisie-ligne">
            {bilan ? (
              <button
                type="button"
                className="secondaire"
                onClick={() => {
                  setBilan(null);
                  setRelecture((n) => n + 1);
                }}
              >
                Relire la revue
              </button>
            ) : null}
            <button type="button" className="secondaire" disabled={enCours} onClick={fermer}>
              {bilan ? 'Fermer' : 'Annuler'}
            </button>
            {!bilan && (
              <button type="button" disabled={!peutCreer} onClick={() => void creer()}>
                {enCours
                  ? 'Création…'
                  : gammesEnvoyees.length > 0
                    ? `Créer ${accordFr(gammesEnvoyees.length, 'brouillon', 'brouillons')}`
                    : 'Créer les brouillons'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Les textes distincts d'une liste de lignes, le plus fréquent d'abord : « KIT entretien vanne… (+1 autre) ». */
function textesDistincts(g: GroupeReferenceTarif): string[] {
  const nombre = new Map<string, number>();
  for (const l of g.lignes) nombre.set(l.designation, (nombre.get(l.designation) ?? 0) + 1);
  return [...nombre.entries()].sort((a, b) => b[1] - a[1]).map(([texte]) => texte);
}

const NATURE_TEXTE: Record<GroupeReferenceTarif['designation'], { libelle: string; classe: string }> = {
  complete: { libelle: 'complété', classe: 'etat actif' },
  reformule: { libelle: 'reformulé', classe: 'etat attente' },
  identique: { libelle: 'identique', classe: 'aide' },
  inutilisable: { libelle: 'texte du tarif inutilisable', classe: 'etat revoque' },
};

function LigneDeReference({
  groupe: g,
  choix,
  droits,
  peutVoirPrix,
  ouverte,
  desactive,
  onBasculer,
  onOuvrir,
}: {
  groupe: GroupeReferenceTarif;
  choix: { designation: boolean; prix: boolean } | undefined;
  droits: DroitsRevue;
  peutVoirPrix: boolean;
  ouverte: boolean;
  desactive: boolean;
  onBasculer: (champ: ChampDecision, valeur: boolean) => void;
  onOuvrir: () => void;
}) {
  const textes = textesDistincts(g);
  const nature = NATURE_TEXTE[g.designation];
  const gammes = new Set(g.lignes.map((l) => l.profileId)).size;
  const prixActuels = [...new Set(g.lignes.flatMap((l) => (l.prix === undefined || l.prix === null ? [] : [l.prix])))];
  const colonnes = 3 + (peutVoirPrix ? 1 : 0) + (droits.peutEcrirePrix ? 1 : 0) + 1;

  return (
    <>
      <tr className={choix?.designation || choix?.prix ? 'maj-gammes-cochee' : undefined}>
        <td className="maj-gammes-case">
          {caseDisponible(g, 'designation', droits) ? (
            <input
              type="checkbox"
              aria-label={`Appliquer le texte du tarif à ${g.reference}`}
              checked={choix?.designation ?? false}
              disabled={desactive}
              onChange={(e) => onBasculer('designation', e.target.checked)}
            />
          ) : (
            <span className="aide">—</span>
          )}
        </td>
        <td>
          <code>{g.reference}</code>
          {g.famille && <div className="aide">{g.famille}</div>}
        </td>
        <td>
          <div className="maj-gammes-texte-actuel">
            {textes.slice(0, 2).map((t) => (
              <div key={t}>{t}</div>
            ))}
            {textes.length > 2 && <div className="aide">… et {textes.length - 2} autre{textes.length - 2 > 1 ? 's' : ''} texte{textes.length - 2 > 1 ? 's' : ''}</div>}
          </div>
          {g.designation !== 'identique' && g.designation !== 'inutilisable' && (
            <div className="maj-gammes-texte-tarif">
              → <strong>{g.description}</strong>
            </div>
          )}
          <span className={nature.classe}>{nature.libelle}</span>
        </td>
        {peutVoirPrix && (
          <td className="maj-gammes-prix">
            {g.prix === undefined || g.prixTarif === undefined ? (
              <span className="aide">—</span>
            ) : g.prix === 'identique' ? (
              <span className="aide">identique ({eurosFr(g.prixTarif)})</span>
            ) : g.prix === 'inutilisable' ? (
              <span className="etat revoque">prix du tarif inutilisable ({eurosFr(g.prixTarif)})</span>
            ) : g.prix === 'manquant' ? (
              <>
                aucun prix → <strong>{eurosFr(g.prixTarif)}</strong>
              </>
            ) : (
              <>
                {prixActuels.map((p) => eurosFr(p)).join(' / ')} → <strong>{eurosFr(g.prixTarif)}</strong>{' '}
                <span className={(g.variationMax ?? 0) > 0 ? 'ecart-hausse' : 'ecart-baisse'}>
                  {formaterVariation(g.variationMax ?? null)}
                </span>
                {variationSuspecte(g.variationMax) && <span className="etat attente"> à vérifier</span>}
              </>
            )}
          </td>
        )}
        {droits.peutEcrirePrix && (
          <td className="maj-gammes-case">
            {caseDisponible(g, 'prix', droits) ? (
              <input
                type="checkbox"
                aria-label={`Appliquer le prix du tarif à ${g.reference}`}
                checked={choix?.prix ?? false}
                disabled={desactive}
                onChange={(e) => onBasculer('prix', e.target.checked)}
              />
            ) : (
              <span className="aide">—</span>
            )}
          </td>
        )}
        <td>
          <button type="button" className="secondaire discret" aria-expanded={ouverte} onClick={onOuvrir}>
            {accordFr(gammes, 'gamme', 'gammes')} · {lignesFr(g.lignes.length)} {ouverte ? '▴' : '▾'}
          </button>
        </td>
      </tr>
      {ouverte && (
        <tr className="maj-gammes-detail">
          <td colSpan={colonnes}>
            <ul className="details-erreur">
              {g.lignes.map((l) => (
                <li key={`${l.profileId}-${l.lineKey}`}>
                  <strong>{l.gammeCode}</strong> · palier {l.levelCode} · « {l.designation} »
                  {peutVoirPrix && l.prix !== undefined && l.prix !== null && <> · {eurosFr(l.prix)}</>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

function BilanDeLaCreation({ bilan }: { bilan: BilanApplicationTarif }) {
  const creees = bilan.gammes.filter((g) => g.brouillon !== null);
  const rienAChanger = bilan.gammes.filter((g) => estRienAChanger(g));
  const refusees = bilan.gammes.filter((g) => g.brouillon === null && !estRienAChanger(g));

  return (
    <div className="maj-gammes-bilan">
      <div className={`message ${creees.length > 0 ? 'succes' : 'avertissement'}`}>
        {messageBilan(bilan)} Date d’effet : <strong>{dateFrTarif(bilan.validFrom)}</strong>.
      </div>
      {creees.length > 0 && (
        <>
          <h3>Brouillons créés ({nombreFr(creees.length)})</h3>
          <ul className="details-erreur">
            {creees.map((g) => (
              <li key={g.profileId}>
                {g.brouillon ? (
                  <a href={lienVersGamme(g.profileId, g.brouillon.id)} target="_blank" rel="noreferrer">
                    {g.code} — brouillon v{g.brouillon.versionNumber}
                  </a>
                ) : (
                  g.code
                )}{' '}
                <span className="aide">
                  ({lignesFr(g.lignes)} modifiée{g.lignes > 1 ? 's' : ''})
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {refusees.length > 0 && (
        <>
          <h3>Gammes refusées ({nombreFr(refusees.length)})</h3>
          <ul className="details-erreur">
            {refusees.map((g) => (
              <li key={g.profileId}>
                <strong>{g.code || g.profileId}</strong> — {g.refus ? `${TITRE_REFUS[g.refus.code]} : ${g.refus.message}` : ''}
              </li>
            ))}
          </ul>
        </>
      )}
      {rienAChanger.length > 0 && (
        <p className="aide">{accordFr(rienAChanger.length, 'gamme', 'gammes')} sans aucun changement pour ces choix.</p>
      )}
    </div>
  );
}
